import React from "react";
import { Clock3, Package, MapPin, Users } from "lucide-react";
import { LoteSugerido } from "../../../services/roteirizacaoService";

interface LotesSugeridosProps {
  lotes: LoteSugerido[];
  selectedLoteId: string | null;
  onSelect: (id: string) => void;
}

function formatRestante(segundos: number | null | undefined): string {
  if (segundos == null || segundos <= 0) return "";
  const m = Math.floor(segundos / 60);
  const s = segundos % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, "0")}` : `${s}s`;
}

const LotesSugeridos: React.FC<LotesSugeridosProps> = ({
  lotes,
  selectedLoteId,
  onSelect,
}) => {
  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15">
          <Package size={20} className="text-primary" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-neutral-900">Lotes sugeridos</h3>
          <p className="text-sm text-neutral-500">
            Casamento por horário + proximidade (Haversine)
          </p>
        </div>
      </div>

      {lotes.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-neutral-200 bg-neutral-50 p-10 text-center">
          <Package size={40} className="mx-auto mb-3 text-neutral-400" />
          <p className="font-medium text-neutral-600">
            Nenhum lote elegível no momento
          </p>
          <p className="mt-1 text-sm text-neutral-400">
            Pedidos precisam estar 100% prontos e geocodificados
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {lotes.map((lote, index) => {
            const selected = lote.id === selectedLoteId;
            const aguardando = lote.liberadoParaSaida === false;
            return (
              <button
                key={lote.id}
                type="button"
                onClick={() => {
                  if (!aguardando) onSelect(lote.id);
                }}
                disabled={aguardando}
                className={`w-full rounded-lg border-2 p-4 text-left transition-all ${
                  aguardando
                    ? "cursor-not-allowed border-amber-200 bg-amber-50/60 opacity-90"
                    : selected
                      ? "border-primary bg-primary/10 shadow-md"
                      : "border-neutral-200 bg-white hover:border-neutral-300"
                }`}
              >
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
                        aguardando
                          ? "bg-amber-200 text-amber-900"
                          : selected
                            ? "bg-primary text-white"
                            : "bg-neutral-100 text-neutral-600"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="font-semibold text-neutral-900">
                      Lote · {lote.quantidadeComandas} comanda
                      {lote.quantidadeComandas !== 1 ? "s" : ""}
                    </span>
                  </div>
                  {aguardando ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-900">
                      <Clock3 size={12} />
                      Aguardando casamento
                      {lote.segundosRestantesAguardar != null
                        ? ` · ${formatRestante(lote.segundosRestantesAguardar)}`
                        : ""}
                    </span>
                  ) : lote.distanciaMaximaEntrePedidosKm != null ? (
                    <span className="flex items-center gap-1 text-xs text-neutral-600">
                      <MapPin size={12} />
                      até {lote.distanciaMaximaEntrePedidosKm} km entre
                      endereços
                    </span>
                  ) : null}
                </div>

                {aguardando && lote.motivoAguardando ? (
                  <p className="mb-2 text-xs text-amber-800">
                    {lote.motivoAguardando}
                  </p>
                ) : null}

                <div className="flex flex-wrap gap-2">
                  {lote.pedidos.map((p) => (
                    <span
                      key={p.pedidoIntegracaoId}
                      className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 text-sm font-medium text-neutral-800 shadow-sm"
                    >
                      #{p.displayId}
                      {p.customerName ? (
                        <span className="text-neutral-500">
                          · {p.customerName}
                        </span>
                      ) : null}
                    </span>
                  ))}
                </div>

                {lote.pedidos.some((p) => p.enderecoResumo) && (
                  <ul className="mt-2 space-y-0.5 text-xs text-neutral-500">
                    {lote.pedidos.map((p) =>
                      p.enderecoResumo ? (
                        <li key={`addr-${p.pedidoIntegracaoId}`}>
                          #{p.displayId}: {p.enderecoResumo}
                        </li>
                      ) : null
                    )}
                  </ul>
                )}
              </button>
            );
          })}
        </div>
      )}

      <p className="mt-4 flex items-center gap-1 text-xs text-neutral-400">
        <Users size={12} />
        Chame o entregador da fila; o modal já vem com as comandas do lote
      </p>
    </div>
  );
};

export default LotesSugeridos;
